/**
 * What a linked URL is allowed to BECOME in a bubble.
 *
 * Almost none of this is a preference — the CSP in `deploy/nginx.conf` already
 * decided it, and a policy the browser enforces beats one we merely intend:
 *
 *   img-src   'self' data: blob: https:   any https image renders. A GIF, an
 *                                         animated WebP and an APNG animate on
 *                                         their own; `<img>` is a media player
 *                                         we already have.
 *   media-src 'self' blob:                a remote `<video>`/`<audio>` src is
 *                                         REFUSED. Not "discouraged" — refused.
 *                                         Inline playback of a linked video is
 *                                         impossible without widening the CSP
 *                                         of a children's classroom.
 *   (no frame-src → default-src 'self')   third-party iframes are refused, so
 *                                         a YouTube embed is not on the table.
 *
 * So a video link gets the honest treatment: a rich card with a real poster and
 * a play affordance that OPENS THE VIDEO OUT of the class. The teacher's actual
 * path to playing a video to the room is screen share, which already carries
 * tab audio ("let the class hear a shared video, not just see it").
 *
 * Who this runs for is decided in the panel, not here: rendering a card means
 * every viewer's browser fetches a third-party origin with no click, which is
 * the same trust boundary that made link preview cards teacher-only.
 */

import { isSafePreviewImage } from './linkify'

/**
 * 'image' renders inline through `<img>` and animates if the format does.
 * 'video' can never render inline here; it becomes a card that opens out.
 */
export type MediaKind = 'image' | 'video'

export interface LinkedMedia {
  kind: MediaKind
  url: string
  /** An https still we may show. For video this is the only thing we CAN show. */
  poster?: string
  /** Display-only provenance for the card ("YouTube"), never a trust signal. */
  provider?: string
}

/** Extensions `<img>` decodes. The animated ones are the point of this file. */
const IMAGE_EXTENSION = /\.(gif|webp|apng|png|jpe?g|avif|bmp|svg)$/i
/** Extensions no `<video>` may load under `media-src 'self' blob:`. */
const VIDEO_EXTENSION = /\.(mp4|webm|mov|m4v|ogv|m3u8|mpd)$/i

/**
 * A YouTube id is 11 chars of the URL-safe alphabet. Matching it exactly (and
 * not "whatever followed v=") is what keeps a crafted link from steering the
 * poster `<img>` at an arbitrary path on the thumbnail host.
 */
const YOUTUBE_ID = /^[\w-]{11}$/

function youtubeId(parsed: URL): string | null {
  const host = parsed.hostname.replace(/^www\./, '')
  if (host === 'youtu.be') {
    const id = parsed.pathname.slice(1).split('/')[0]
    return YOUTUBE_ID.test(id) ? id : null
  }
  if (host !== 'youtube.com' && host !== 'm.youtube.com' && host !== 'music.youtube.com')
    return null
  const watch = parsed.searchParams.get('v')
  if (watch && YOUTUBE_ID.test(watch)) return watch
  // /embed/<id> and /shorts/<id> are the two other shapes a teacher actually pastes.
  const path = parsed.pathname.split('/').filter(Boolean)
  if ((path[0] === 'embed' || path[0] === 'shorts' || path[0] === 'live') && path[1]) {
    return YOUTUBE_ID.test(path[1]) ? path[1] : null
  }
  return null
}

/**
 * Classify a link, or return null when it is just a link.
 *
 * `isSafePreviewImage` is the gate for everything that renders (https only, no
 * embedded credentials, no IP-literal host) — the same check the existing
 * preview card applies to its thumbnail, reused rather than re-derived so the
 * two can never drift apart.
 */
export function classifyMedia(url: string | null | undefined): LinkedMedia | null {
  if (!url || !isSafePreviewImage(url)) return null
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  const id = youtubeId(parsed)
  if (id) {
    // hqdefault exists for every video; maxres does not, and a missing poster
    // renders as a broken image inside the card.
    return {
      kind: 'video',
      url,
      poster: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      provider: 'YouTube',
    }
  }

  const path = parsed.pathname
  if (VIDEO_EXTENSION.test(path)) {
    const host = parsed.hostname.replace(/^www\./, '')
    return { kind: 'video', url, provider: host === 'vimeo.com' ? 'Vimeo' : host }
  }
  if (IMAGE_EXTENSION.test(path)) {
    return { kind: 'image', url, poster: url }
  }
  // Vimeo/Drive/etc. carry no extension and need a server unfurl to say anything
  // useful; the existing preview card already covers that path.
  return null
}

/** True when the URL is a format that animates on its own inside `<img>`. */
export function isAnimatedFormat(url: string): boolean {
  try {
    return /\.(gif|webp|apng)$/i.test(new URL(url).pathname)
  } catch {
    return false
  }
}
